import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { formatApiError } from "@/lib/utils";
import { UserPlus, Save, X, Trash2, KeyRound, Pencil, ShieldOff, ShieldCheck, CheckCircle2 } from "lucide-react";

const ROLES = [
  "super_admin",
  "top_management",
  "qc_manager",
  "factory_user",
  "baikampady_user",
  "maghveera_user",
];

const blank = { email: "", password: "", name: "", role: "factory_user" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [edit, setEdit] = useState(null);
  const [reset, setReset] = useState(null); // {user, newPassword}
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const createUser = async () => {
    setErr("");
    try {
      await api.post("/users", form);
      setForm(blank); setCreateOpen(false);
      setMsg(`✓ User '${form.email}' created`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const saveEdit = async () => {
    setErr("");
    try {
      await api.patch(`/users/${edit.id}`, { name: edit.name, role: edit.role });
      setEdit(null);
      setMsg("✓ User updated");
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const doReset = async () => {
    setErr("");
    try {
      await api.post(`/users/${reset.user.id}/reset-password`, { new_password: reset.newPassword });
      setReset(null);
      setMsg(`✓ Password reset for ${reset.user.email}`);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const toggleDisabled = async (u) => {
    setErr("");
    try {
      await api.patch(`/users/${u.id}`, { disabled: !u.disabled });
      setMsg(`✓ ${u.email} ${u.disabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    setErr("");
    try {
      await api.delete(`/users/${u.id}`);
      setMsg(`✓ ${u.email} deleted`);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        overline="ADMIN"
        title="User Management"
        description="Create, rename, disable, or delete user accounts. Reset passwords. Every change is attributed by name to the admin who made it."
        actions={
          <button className="btn btn-primary" onClick={() => { setForm(blank); setErr(""); setCreateOpen(true); }} data-testid="open-create-user">
            <UserPlus size={16} /> Add User
          </button>
        }
      />

      {msg && (
        <div className="card p-3 mb-4 text-xs flex items-center gap-2" style={{ borderColor: "var(--green)", color: "var(--green)", background: "rgba(34,197,94,0.1)" }} data-testid="users-success">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}
      {err && (
        <div className="card p-3 mb-4 text-xs" style={{ borderColor: "var(--red)", color: "var(--red)", background: "rgba(239,68,68,0.1)" }} data-testid="users-error">
          {err}
        </div>
      )}

      <div className="card overflow-hidden">
        <div style={{ overflowX: "auto" }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan="6" className="text-center py-8" style={{ color: "var(--text-muted)" }}>No users.</td></tr>}
              {users.map((u) => {
                const isSelf = u.id === user?.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="font-medium">{u.name}{isSelf && <span className="ml-2 text-xs" style={{ color: "var(--orange)" }}>(you)</span>}</td>
                    <td className="font-mono text-xs">{u.email}</td>
                    <td className="font-mono text-xs">{ROLE_LABELS[u.role] || u.role}</td>
                    <td>
                      {u.disabled ? (
                        <span className="badge badge-rejected">DISABLED</span>
                      ) : (
                        <span className="badge badge-approved">ACTIVE</span>
                      )}
                    </td>
                    <td className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>{u.created_at?.slice(0, 10)}</td>
                    <td>
                      <div className="flex gap-1 justify-end">
                        <button
                          className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11, color: "var(--blue)" }}
                          onClick={() => setEdit({ ...u })}
                          data-testid={`edit-user-${u.email}`}
                          title="Edit name/role"
                        ><Pencil size={12} /></button>
                        <button
                          className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11, color: "var(--amber)" }}
                          onClick={() => setReset({ user: u, newPassword: "" })}
                          data-testid={`reset-user-${u.email}`}
                          title="Reset password"
                        ><KeyRound size={12} /></button>
                        {!isSelf && (
                          <>
                            <button
                              className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11 }}
                              onClick={() => toggleDisabled(u)}
                              data-testid={`toggle-user-${u.email}`}
                              title={u.disabled ? "Enable" : "Disable"}
                            >{u.disabled ? <ShieldCheck size={12} color="var(--green)" /> : <ShieldOff size={12} color="var(--amber)" />}</button>
                            <button
                              className="btn btn-ghost" style={{ padding: "4px 8px", fontSize: 11, color: "var(--red)" }}
                              onClick={() => doDelete(u)}
                              data-testid={`delete-user-${u.email}`}
                              title="Delete user"
                            ><Trash2 size={12} /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setCreateOpen(false)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="create-user-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono mb-1" style={{ color: "var(--orange)" }}>// NEW USER</div>
                <div className="font-heading text-xl font-bold">Add User</div>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setCreateOpen(false)}><X size={16} /></button>
            </div>
            <label>Full Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ramesh Kumar" data-testid="new-user-name" />
            <label style={{ marginTop: 10 }}>Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ramesh@smmarine.co" data-testid="new-user-email" />
            <label style={{ marginTop: 10 }}>Password</label>
            <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min 6 characters" data-testid="new-user-password" />
            <label style={{ marginTop: 10 }}>Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role">
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <div className="flex gap-3 mt-5">
              <button className="btn btn-secondary flex-1" onClick={() => setCreateOpen(false)}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={createUser} disabled={!form.name || !form.email || !form.password} data-testid="submit-new-user">
                <UserPlus size={14} /> Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setEdit(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono mb-1" style={{ color: "var(--blue)" }}>// EDIT USER</div>
                <div className="font-heading text-xl font-bold">{edit.email}</div>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setEdit(null)}><X size={16} /></button>
            </div>
            <label>Full Name</label>
            <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-user-name" />
            <label style={{ marginTop: 10 }}>Role</label>
            <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} data-testid="edit-user-role">
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <div className="flex gap-3 mt-5">
              <button className="btn btn-secondary flex-1" onClick={() => setEdit(null)}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={saveEdit} data-testid="save-user-edit">
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {reset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => setReset(null)}>
          <div className="card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs font-mono mb-1" style={{ color: "var(--amber)" }}>// RESET PASSWORD</div>
                <div className="font-heading text-xl font-bold">{reset.user.email}</div>
              </div>
              <button className="btn btn-ghost" style={{ padding: 4 }} onClick={() => setReset(null)}><X size={16} /></button>
            </div>
            <label>New Password</label>
            <input
              type="text"
              value={reset.newPassword}
              onChange={(e) => setReset({ ...reset, newPassword: e.target.value })}
              placeholder="min 6 characters"
              data-testid="reset-password-input"
            />
            <div className="text-xs mt-3" style={{ color: "var(--text-muted)" }}>
              Share this new password with the user securely. They can change it themselves from their profile later.
            </div>
            <div className="flex gap-3 mt-5">
              <button className="btn btn-secondary flex-1" onClick={() => setReset(null)}>Cancel</button>
              <button className="btn btn-primary flex-1" onClick={doReset} disabled={(reset.newPassword || "").length < 6} data-testid="submit-reset-password">
                <KeyRound size={14} /> Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
